/* SUMMARY
  * Imports
  * SuspensionReasonBadge: why a suspended intervention was paused
*/

/* Imports */
import { Typography } from "@material-tailwind/react";
import { t } from "i18next";
import { getWorkorderStatusStyle } from "./workorderStatusStyles";
import { WorkorderStatus } from "../../../types/workorder/workorderStatus";
import { FeatureFlagKey, useFeatureFlag } from "../../../hooks/useFeatureFlag";
/***/

/**
 * SuspensionReasonBadge: the reason a technician gave when pausing an intervention.
 *
 * This is the point of the whole "intervention on hold" feature from the planner's side: the
 * office used to have to call the technician to find out why a job was left open. The badge is
 * what lets a planner read the answer instead of chasing it down.
 *
 * The server always serializes `suspensionReason`/`suspensionNote` (no @JsonInclude(NON_NULL)), so
 * both are present-but-null for an intervention that is not paused. Renders nothing in that case,
 * so a caller can pass the fields straight through without a status check of its own.
 *
 * Also renders nothing while the technician_pause flag is off: the mobile app then pauses without
 * asking for a reason, and the board keeps the look it had before the badge.
 *
 * @param reason - the technician's reason, or null for an intervention that is not paused
 * @param note - the technician's free-text note, or null when they left it blank
 * @param className - additional classnames
 */
export default function SuspensionReasonBadge({
  reason,
  note,
  className = ""
}: {
  reason?: EasyTech.SuspensionReason | null,
  note?: string | null,
  className?: string
}) {
  const isTechnicianPauseEnabled = useFeatureFlag(FeatureFlagKey.TECHNICIAN_PAUSE);

  if (!reason || !isTechnicianPauseEnabled) return null;

  const style = getWorkorderStatusStyle(WorkorderStatus.SUSPENDED);

  return (
    <div
      className={`inline-flex max-w-full flex-col gap-0.5 rounded-md px-2 py-1 ${style.softClass} ${className}`}
      data-testid="suspension-reason-badge"
    >
      <Typography variant="small" className={`font-medium ${style.textClass}`}>
        {t(`planification:suspensionReason.${reason}`)}
      </Typography>

      {note && (
        <Typography variant="small" className={`${style.textClass} break-words whitespace-pre-line`}>
          {note}
        </Typography>
      )}
    </div>
  );
}
/***/
